import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useAutoSave, useToggle } from '~/hooks';
import { IJobDetailsForm } from '../../JobDetailsForm';
import { useSaveJobPost } from './useSaveJobPost';

export const useAutoSaveDraftJobPost = (
  controller: UseFormReturn<IJobDetailsForm>,
  jobPostingId: string | undefined,
) => {
  const [isShowSavedMessage, controls] = useToggle();

  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isShowSavedMessageRef = useRef(isShowSavedMessage);

  const { isPending, onSave } = useSaveJobPost(controller, jobPostingId, {
    triggers: [],
    withRedirect: false,
  });

  const { lastSavedAt } = useAutoSave({
    watch: controller.watch,
    saveFn: onSave,
  });

  // Keep ref in sync with state
  useEffect(() => {
    isShowSavedMessageRef.current = isShowSavedMessage;
  }, [isShowSavedMessage]);

  // Handle auto-save status display with dismiss-and-reshow on new saves
  useEffect(() => {
    if (!lastSavedAt) return;

    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    const isCurrentlyVisible = isShowSavedMessageRef.current;

    if (isCurrentlyVisible) {
      // Hide first to trigger exit animation
      controls.setOff();
    }

    // Delay showing only if we need to wait for exit animation
    const showDelay = isCurrentlyVisible ? 450 : 0;

    const showTimeoutId = setTimeout(() => {
      controls.setOn();

      // Schedule hide after 3 seconds
      hideTimeoutRef.current = setTimeout(() => {
        controls.setOff();
      }, 3000);
    }, showDelay);

    return () => {
      clearTimeout(showTimeoutId);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSavedAt]);

  return { isLoading: isPending, isShowSavedMessage, lastSavedAt };
};
